import { appState } from "../config/localStorage";
import { subscribeRuntimeContext } from "../config/runtimeAccessor";
import { getLocalStorageItem } from "./localStorageHelper";
import { ROUTES_PATH } from "../utils/EndPoints";
import { defaultHistory } from "../history";

const computePermissionsAndRoles = () => {
  const user = getLocalStorageItem("user");
  return {
    permissionsValue: user?.permissions ?? [],
    rolesValue: user?.roles ?? [],
  };
};

let derivedAuth = computePermissionsAndRoles();

// See config/localStorage.js — exported as a getter-backed `const` object
// (rather than reassigned `export let` — SonarQube S6861) so consumers read the
// latest value via `authState.permissions` / `authState.roles`.
export const authState = {
  get permissions() { return derivedAuth.permissionsValue; },
  get roles() { return derivedAuth.rolesValue; },
};

// Was `...permissions` (the module-level export above) instead of
// `...(user_metadata?.permissions || [])` — that module-level value only
// updates via the subscribeRuntimeContext listener at the bottom of this
// file, itself only notified once something calls setAppRuntimeContext
// (AuthContext's syncLocalStore, inside a React setState updater) — which
// runs on React's own schedule, not synchronously with whatever just called
// this function. PrivateRoute calls this with AuthContext's own `user`
// (already fresh at render time, no such lag) right after a fresh login —
// reading the stale module-level snapshot here instead meant a route
// processLogin had *just* correctly resolved and navigated to (via getPath,
// which this same login had already made permissions available for) could
// still get rejected by PrivateRoute's own permission check for the
// permissions this exact login granted, bouncing to /error before the lag
// caught up (a subsequent refresh, forcing a fresh read, showed the correct
// page instead).
// custom_permissions is keyed by module ({ edm: [...], <any future key>: [...] })
// but can also arrive as a JSON string of that object, or be absent —
// Object.values() on a string yields its characters. Normalise it to a
// { key: string[] } object, keeping every key.
export const normalizeCustomPermissions = (customPermissions) => {
  let value = customPermissions;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).map(([key, perms]) => [
      key,
      (Array.isArray(perms) ? perms.flat() : [perms]).filter((perm) => typeof perm === "string"),
    ])
  );
};

const customPermissionsList = (customPermissions) =>
  Object.values(normalizeCustomPermissions(customPermissions)).flat();

export const permissionsFn = (user_metadata = {}) =>
  [...new Set([
    ...(user_metadata?.permissions || []),
    ...customPermissionsList(user_metadata?.custom_permissions),
  ])];

subscribeRuntimeContext(() => {
  derivedAuth = computePermissionsAndRoles();
});

export const findRouteDetails = (route, structure) => {
  for (const [key, value] of Object.entries(structure.child || {})) {
    if (value.route === route) {
      return { parent: structure.name, menu: key, subMenu: null };
    }
    if (value.child) {
      const result = findRouteDetails(route, value);
      if (result) {
        return { parent: structure.name, menu: key, subMenu: result.menu };
      }
    }
  }
  if (structure.childrenRoutes && structure.childrenRoutes?.some((children) => pathMatches(children, route))) {
    return { parent: structure.name, menu: null, subMenu: null };
  }
  return null;
};

export const accessibleMenus = (menu_items) => {
  const permissions = permissionsFn(appState.user_metadata);
  return menu_items?.filter(
    (menu) =>
      menu?.permissions?.some((ele) => permissions?.includes(ele)) &&
      (!menu?.roles?.length || authState.roles?.includes(menu.roles[0]))
  );
};

export const processNodeAndPermissions = (node) => {
  if (!node) return { routes: [], permissions: [] };
  const routes = node.route ? [node.route] : [];
  let permissions = [...(node.permissions || [])];
  if (node.child) {
    for (const child of Object.values(node.child)) {
      const { routes: childRoutes, permissions: childPermissions } =
        processNodeAndPermissions(child);
      routes.push(...childRoutes);
      permissions = [...new Set([...permissions, ...childPermissions])];
    }
  }

  if (node.extraRoutes) {
    for (const route of node.extraRoutes) {
      if (route.route) {
        routes.push(route.route);
      }
      permissions = [
        ...new Set([...permissions, ...(route.permissions || [])]),
      ];
    }
  }
  if (node.child) {
    node.childrenRoutes = routes;
  }
  node.permissions = [...new Set([...node.permissions, ...permissions])];
  return { routes, permissions };
};

export const generateRouteAndPermissions = (arr) => {
  const master = {};
  const addRoute = (route, permissions) => {
    if (route && permissions.length > 0) {
      master[route] = { route, permissions };
    }
  };

  const traverse = (obj) => {
    if (!obj?.childrenRoutes) addRoute(obj.route, obj.permissions);
    if (obj?.extraRoutes) {
      obj?.extraRoutes?.forEach(({ route, permissions }) => {
        addRoute(route, permissions);
      });
    }
    if (obj.child) {
      Object.values(obj.child).forEach(traverse);
    }
  };

  arr?.forEach((node) => {
    traverse(node);
    node?.extraRoutes?.forEach(({ route, permissions }) => {
      addRoute(route, permissions);
    });
  });

  return master;
};

export const redirectToPath = (menu) => {
  // A missing/unresolved menu (e.g. getPath found no accessible route for
  // this user's permissions — a real case for a not-yet-migrated remote
  // whose menu entries getPath doesn't recognize) used to be concatenated in
  // anyway, sending the browser to a URL literally containing "undefined"
  // (ROUTES_PATH.SCAI + "undefined"). Fall back to the app root instead.
  if (!menu?.route) {
    defaultHistory.push(ROUTES_PATH.ROUTE);
    return;
  }
  const _path = `${ROUTES_PATH.SCAI}${menu.route}`;
  defaultHistory.push(_path);
};

export const getActiveMenu = ({ item, path }) => {
  if (item?.child) {
    return item?.childrenRoutes?.includes(path)
  } else {
    return item?.route === path
  }
}

export const pathMatches = (item, route) => {
  const regexStr = "^" + item.replace(/:[^/]+/g, "[^/]+") + "$";
  return new RegExp(regexStr).test(route);
};

export const getFirstAccessibleRoute = (node) => {
  if (!node?.child) return null;
  for (const child of Object.values(node.child)) {
    if (child.route && (!child.permissions?.length || child.permissions.some((p) => authState.permissions.includes(p)))) {
      return child.route;
    }
    const nested = getFirstAccessibleRoute(child);
    if (nested) return nested;
  }
  return null;
};